import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import useAuthStore from '@entities/auth/model/useAuthStore';

import ContentSectionLayout from '@shared/components/organisms/ContentSectionLayout/ContentSectionLayout';

import DocumentDetailModal from '@pages/approval-box/components/DocumentDetailModal/DocumentDetailModal';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import { MobileLayout } from '@shared/components/organisms/mobile';
import { MobileDrafterContent, MobileDocumentDetail } from '@pages/approval-box/components/mobile';

import useApprovalSection from '@features/approval/hooks/useApprovalSection';
import { useGetPcApprovalDocListQuery } from '@features/approval/hooks';
import { useDocFormListSimpleQuery } from '@features/form-selection/hooks';
import { getFilterFields } from '@features/approval/components/drafter/config/filterFields';
import { tableColumns } from '@features/approval/components/drafter/config/tableColumns';

import Alert from '@shared/components/atoms/Alert';
import useAlert from '@shared/hooks/useAlert';
import { useIsMobile } from '@shared/hooks';
import createExcelFile from '@shared/lib/excel';
import { formatDate, getDefaultDateRange } from '@shared/utils/formatDate';

import styles from './DrafterPage.module.scss';
/**
 * DrafterPage 컴포넌트
 * - 기안자 상신함 페이지
 * - 테스트 데이터로 문서 목록 표시
 *  - done : 기결
 *  - futureIng : 예결
 *  - approvalIng : 미결
 */
export default function DrafterPage() {
  const queryClient = useQueryClient();
  // 모바일 화면 감지
  const isMobile = useIsMobile();

  /** 문서상세 모달 닫기 시 리스트 재갱신 */
  const closeDetailModal = useCallback(
    async function () {
      setIsModalOpen(false);
      setSelectedDocIdx(null);
      queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });
      queryClient.invalidateQueries({ queryKey: ['approvalList'] });
      await queryClient.refetchQueries({ queryKey: ['pcApprovalList'] });
      await queryClient.refetchQueries({ queryKey: ['approvalList'] });
    },
    [queryClient],
  );

  // 비밀번호 변경 권유 Alert 전용 상태
  const [pwAlertOpen, setPwAlertOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.showPasswordChangeAlert) {
      setPwAlertOpen(true);
    }
  }, [location.state]);

  const pwAlertMessage = (
    <div className={styles.pwAlert}>
      <div className={styles.pwAlertTitle}>개인정보 보호를 위해 비밀번호를 변경해 주세요</div>

      <ul className={styles.pwAlertDesc}>
        <li>
          개인정보 보호를 위해 비밀번호는 3개월마다 변경하는 것이 안전합니다.
          <br />
          안전한 사용을 위해 비밀번호 변경을 권장드립니다.
        </li>
      </ul>
    </div>
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocIdx, setSelectedDocIdx] = useState(null);
  /**
   * TODO
   *  - 탭의 문서 갯수는 신규 결재 문서 발생 시 증가하며 해당 문서를 결재, 반려, 수정요청 하는 시점에 감소
   */

  /** Alert 상태 관리 */
  const { alertState, showAlert, closeAlert } = useAlert();

  /** 로그인된 사용자 회사 코드 조회  */
  const companyCode = useAuthStore((state) => state.companyCode);

  /* =======================
   * 기본 조회 기간 (3개월)
   * ======================= */
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  /* =======================
   * 🔑 searchParams (탭별)
   * ======================= */
  const [submittedSearchParams, setSubmittedSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  const [rejectedSearchParams, setRejectedSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  /**
   * 양식 목록 조회
   * - formOptions로 변환하여 필터 Select 박스에 사용
   * - docFormListSimple API 사용 (form_data 제외)
   */
  const { data: docFormList = [] } = useDocFormListSimpleQuery(companyCode);

  /** 필터에서 사용될 문서 양식 선택 옵션 구성 */
  const formOptions = useMemo(
    function () {
      return [
        { label: '전체', value: '' },
        ...docFormList.map(function (item) {
          return { label: item.title, value: item.doc };
        }),
      ];
    },
    [docFormList],
  );

  /** excel 의 날짜 데이터 format */
  const excelFormatter = useCallback(function (rows) {
    return rows.map(function (r) {
      return {
        ...r,
        date: formatDate(r.date),
        corpSealUsed: r.is_corp_seal_used === 1 || r.is_corp_seal_used === '1' ? 'Y' : 'N',
      };
    });
  }, []);

  const getExtendedFilterFields = useCallback(function (filterValues, currentFormOptions) {
    // 1. 기본 필터 생성
    const baseFields = getFilterFields(filterValues, currentFormOptions);

    // 2. 문서상태(stateCode) 필드 추출
    const stateField = baseFields.find(function (field) {
      return field.key === 'stateCode';
    });

    if (!stateField) {
      // 방어 코드 (확실하지 않음 → but 안전)
      return baseFields;
    }

    // 3. 기존 stateCode 제거
    const withoutState = baseFields.filter(function (field) {
      return field.key !== 'stateCode';
    });

    // 4. docType 위치 찾기
    const docTypeIndex = withoutState.findIndex(function (field) {
      return field.key === 'docType';
    });

    if (docTypeIndex === -1) {
      // docType 없으면 원본 반환
      return withoutState;
    }

    // 5. docType 뒤에 stateCode 삽입
    const reordered = [
      ...withoutState.slice(0, docTypeIndex + 1),
      stateField,
      ...withoutState.slice(docTypeIndex + 1),
    ];

    return reordered;
  }, []);

  const defaultTab = useMemo(
    function () {
      const params = new URLSearchParams(location.search);
      const tab = params.get('tab');
      return tab || 'submitted';
    },
    [location.search],
  );

  // 현재 선택된 탭 상태 (DocumentDetailModal에 tabType 전달용, useApprovalSection isActiveTab용)
  const [currentTab, setCurrentTab] = useState(defaultTab);

  /**
   * 반려함 목록 조회에 필요한 공통 로직
   * - queryType: 'return'  반려 문서 조회
   * - filterFieldBuilder  필터 UI 구성 함수
   * - useQueryHook  React Query 기반 API 조회 훅
   * - tableColumns  테이블 컬럼 설정
   * - formOptions  양식 Select 옵션
   */
  const rejectedSection = useApprovalSection({
    queryType: 'return',
    searchParams: rejectedSearchParams,
    setSearchParams: setRejectedSearchParams,
    filterFieldBuilder: getExtendedFilterFields,
    tableColumns: tableColumns,
    useQueryHook: useGetPcApprovalDocListQuery,
    formOptions,
    excelFormatter,
    defaultSort: { field: 'date', direction: 'desc' },
    isActiveTab: currentTab === 'rejected',
  });

  /**
   * 상신함 목록 조회에 필요한 공통 로직
   * - queryType: 'ing'  상신 문서 조회
   * - filterFieldBuilder  필터 UI 구성 함수
   * - useQueryHook  React Query 기반 API 조회 훅
   * - tableColumns  테이블 컬럼 설정
   * - formOptions  양식 Select 옵션
   */
  const submittedSection = useApprovalSection({
    queryType: 'ing',
    searchParams: submittedSearchParams,
    setSearchParams: setSubmittedSearchParams,
    filterFieldBuilder: getExtendedFilterFields,
    tableColumns: tableColumns,
    useQueryHook: useGetPcApprovalDocListQuery,
    formOptions,
    excelFormatter,
    defaultSort: { field: 'date', direction: 'desc' },
    isActiveTab: currentTab === 'submitted',
  });

  // 2026.02.23 엑셀용 컬럼 생성
  const excelColumns = useMemo(() => {
    return [
      ...tableColumns,

      {
        key: 'corpSealUsed',
        label: '법인인감사용',
        render: (_, row) =>
          row.is_corp_seal_used === 1 || row.is_corp_seal_used === '1' ? 'Y' : 'N',
      },
    ];
  }, [tableColumns]);

  // 행 클릭 핸들러 (코멘트 확인 API 제거 - 모달 내 useDocumentCommentListQuery에서 조회)
  function handleRowClick(item) {
    const docIdx = item.idx;
    if (!docIdx) {
      console.warn('문서 인덱스를 찾을 수 없습니다:', item);
      return;
    }

    setSelectedDocIdx(docIdx);
    setIsModalOpen(true);
  }

  // 모바일 레이아웃 렌더링
  if (isMobile) {
    return (
      <MobileLayout title="기안문서">
        <MobileDrafterContent
          activeTab={currentTab}
          onTabChange={setCurrentTab}
          submittedSection={submittedSection}
          rejectedSection={rejectedSection}
          onItemClick={handleRowClick}
          formOptions={formOptions}
        />
        {/* 모바일 문서 상세 (PC와 다르게 본문 이미지 사용) */}
        <MobileDocumentDetail
          isOpen={isModalOpen}
          onClose={closeDetailModal}
          docIdx={selectedDocIdx}
          userRole="drafter"
          tabType={currentTab === 'submitted' ? 'ing' : 'return'}
        />
        {/* Alert 컴포넌트 */}
        <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
        <Alert
          isOpen={pwAlertOpen}
          mode="confirm"
          message={pwAlertMessage}
          confirmText="비밀번호 변경"
          cancelText="나중에"
          onConfirm={() => {
            setPwAlertOpen(false);
            navigate('/approval/settings/password');
          }}
          onClose={() => {
            setPwAlertOpen(false);
          }}
        />
      </MobileLayout>
    );
  }

  // PC 레이아웃 렌더링
  return (
    <MainLayout>
      <div className={styles.container}>
        <TabContentLayout
          lazyLoad
          tabs={[
            {
              value: 'submitted',
              label: '상신함',
              content: (
                <ContentSectionLayout
                  {...submittedSection}
                  classes={styles}
                  showExcelButton={true}
                  emptyMessage="현재 상신 문서가 없습니다."
                  onExcel={() =>
                    createExcelFile({
                      rows: submittedSection.handleExcel(),
                      fileName: '상신함.xlsx',
                      columns: excelColumns,
                    })
                  }
                  onRowClick={handleRowClick}
                />
              ),
            },
            {
              value: 'rejected',
              label: '반려함',
              content: (
                <ContentSectionLayout
                  {...rejectedSection}
                  classes={styles}
                  showExcelButton={true}
                  emptyMessage="현재 반려 문서가 없습니다."
                  onExcel={() =>
                    createExcelFile({
                      rows: rejectedSection.handleExcel(),
                      fileName: '반려함.xlsx',
                      columns: excelColumns,
                    })
                  }
                  onRowClick={handleRowClick}
                />
              ),
            },
          ]}
          defaultTab={defaultTab}
          onTabChange={setCurrentTab}
        />
      </div>
      {/* 문서 상세 모달 */}
      <DocumentDetailModal
        isOpen={isModalOpen}
        onClose={closeDetailModal}
        docIdx={selectedDocIdx}
        userRole="drafter" // 기안자이므로 'drafter'
        tabType={currentTab === 'submitted' ? 'ing' : 'return'}
      />
      {/* Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />

      <Alert
        isOpen={pwAlertOpen}
        mode="confirm"
        message={pwAlertMessage}
        confirmText="비밀번호 변경"
        cancelText="나중에"
        onConfirm={() => {
          setPwAlertOpen(false);
          navigate('/approval/settings/password'); // 비밀번호 변경 페이지
        }}
        onClose={() => {
          setPwAlertOpen(false); // 나중에
        }}
      />
    </MainLayout>
  );
}
